import {  computed, effect, inject, Injectable, resource, signal, type Signal} from '@angular/core';
import { ApiService } from '@app/core/api.service';
import {type ICreateChangeSubscriptionEventRequest, type IExecuteChangeSubscriptionEventRequest, ISubscriptionSchedule, type ISubscriptionPlan, type ICurrentSubscription, type IV2CreateChangeEventRequest, type IV2PreviewResponse, type IV2ExecuteChangeEventRequest, type IV2ExecuteResponse} from '@models/subscriptions.model';
import { SessionService } from './session.service';
import { WorkspaceService } from './workspace.service';
import { SupabaseService } from './supabase.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { of, from } from 'rxjs';
import { DbTable } from '@app/models/models.helpers';
import { Database } from '@app/types/supabase';

export interface ISubscriptionSummary {
  price_name: string;
  full_price: number;
  status: Database['public']['Enums']['subscriptionstatus'];
  description: string;
  cancel_at: Date | null;
  renew_at: Date | null;
  current_period_end: Date;
  schedule: ISubscriptionSchedule | null;
  plan: DbTable<'subscription_plans'> | null;
  scheduled: {
    price_id: string;
    price_name: string;
    start_date: Date;
    end_date: Date;
  } | null;
  actions: {
    label: string;
    severity: 'info' | 'danger' | 'warning' | 'success';
    callback: () => void;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionsService {
  api = inject(ApiService);
  sessionService = inject(SessionService);
  workspaceService = inject(WorkspaceService);
  supabase = inject(SupabaseService);

  plans = signal<DbTable<'subscription_plans'>[]>([]);

  readonly activeSubscriptionResource = rxResource({
    params: () => ({ billingAccountId: this.workspaceService.billingAccountId() }),
    stream: ({params}) => {
      if (!params.billingAccountId) return of(null);
      return from(this.supabase.client
        .from('subscriptions')
        .select('*, plan:subscription_plans(*)')
        .eq('billing_account_id', params.billingAccountId)
        .in('status', ['ACTIVE', 'TRIALING', 'PAST_DUE', 'CANCELED'])
        // .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) {
            console.error('Error loading subscription', error);
            return null;
          }
          return data;
        }));
    }
  });

  constructor() {
    this.loadBillingPlans();
  }

  async loadBillingPlans(): Promise<DbTable<'subscription_plans'>[]> {
    // if (this.plans().length === 0) {
    //   const res = await this.api.aget<ISubscriptionPlan[]>(`subscriptions/plans`);
    //   this.plans.set(res.sort((a, b) => a.unit_amount - b.unit_amount));
    // }
    return this.plans();
  }

  async getBillingInvoices(): Promise<DbTable<'subscription_invoices'>[]> {
    return await this.api.aget<DbTable<'subscription_invoices'>[]>(`subscriptions/invoices`);
  }

  async previewChangeSubscriptionEvent(body:ICreateChangeSubscriptionEventRequest):Promise<DbTable<'subscription_change_events'>> {
    return await this.api.apost<DbTable<'subscription_change_events'>>(`subscriptions/create-change-subscription-event`, body);
  }
  async executeChangeSubscriptionEvent(body:IExecuteChangeSubscriptionEventRequest):Promise<DbTable<'subscription_change_events'>> {
    return await this.api.apost<DbTable<'subscription_change_events'>>(`subscriptions/execute-change-subscription-event`, body);
  }

  // --- V2 API Methods ---

  v2Plans = signal<ISubscriptionPlan[]>([]);
  v2CurrentSubscription = signal<ICurrentSubscription | null>(null);
  v2Loading = signal(false);
  v2Error = signal<string | null>(null);

  async loadV2Plans(workspaceId: string): Promise<ISubscriptionPlan[]> {
    try {
      const res = await this.api.aget<ISubscriptionPlan[]>('subscriptions/plans', { workspace_id: workspaceId });
      const sorted = (res || []).sort((a, b) => a.unit_amount - b.unit_amount);
      this.v2Plans.set(sorted);
      return sorted;
    } catch (err) {
      console.error('Error loading V2 plans', err);
      this.v2Error.set('Failed to load subscription plans.');
      return [];
    }
  }

  async loadV2CurrentSubscription(workspaceId: string): Promise<ICurrentSubscription | null> {
    try {
      const res = await this.api.aget<ICurrentSubscription>('subscriptions/current', { workspace_id: workspaceId });
      this.v2CurrentSubscription.set(res ?? null);
      return res;
    } catch (err) {
      console.error('Error loading V2 current subscription', err);
      this.v2Error.set('Failed to load current subscription.');
      return null;
    }
  }

  async loadV2All(workspaceId: string): Promise<void> {
    this.v2Loading.set(true);
    this.v2Error.set(null);
    try {
      await Promise.all([
        this.loadV2Plans(workspaceId),
        this.loadV2CurrentSubscription(workspaceId),
      ]);
    } finally {
      this.v2Loading.set(false);
    }
  }

  v2CurrentPlan = computed<ISubscriptionPlan | null>(() => {
    const sub = this.v2CurrentSubscription();
    if (!sub?.plan) return null;
    return sub.plan;
  });

  async v2PreviewChange(workspaceId: string, body: IV2CreateChangeEventRequest): Promise<IV2PreviewResponse> {
    return await this.api.apost<IV2PreviewResponse>(
      `subscriptions/create-change-subscription-event?workspace_id=${encodeURIComponent(workspaceId)}`,
      body
    );
  }

  async v2ExecuteChange(workspaceId: string, body: IV2ExecuteChangeEventRequest): Promise<IV2ExecuteResponse> {
    return await this.api.apost<IV2ExecuteResponse>(
      `subscriptions/execute-change-subscription-event?workspace_id=${encodeURIComponent(workspaceId)}`,
      body
    );
  }

  subscriptionSummary: Signal<ISubscriptionSummary | null> = computed(() => {
    const plans = this.plans();
    // Fetch subscription from the resource instead of the workspace signal
    const subscription: any = this.activeSubscriptionResource.value();
    
    if (!subscription) return null; 
    // Ensure subscription has plan data (it should from the join)
    if (!subscription.plan) return null;

    const renew_at = new Date(subscription.current_period_end);
    const ret: ISubscriptionSummary = {
      price_name: subscription.plan.price_name,
      full_price: subscription.plan.unit_amount / 100,
      status: subscription.status,
      description: `Your subscription will automatically renew on ${renew_at.toLocaleDateString()}.`,
      cancel_at: subscription.cancel_at ? new Date(subscription.cancel_at) : null,
      renew_at: renew_at,
      current_period_end: subscription.current_period_end,
      scheduled: null,
      schedule: subscription.schedule,
      plan: subscription.plan,
      actions: [],
    };
    
    if (ret.cancel_at) {
      ret.description = `Your subscription has been canceled and will be active until ${ret.cancel_at.toLocaleDateString()}.`;
    }
    else if (subscription.schedule && subscription.schedule.phases.length > 0) {
      const futurePriceId = null;
      let futurePriceName = null;
      let changeDate = null;
      for (const phase of subscription.schedule.phases) {
        // if is future phase
        if (new Date(phase.start_date * 1000) > new Date()) {
          if (plans) {
             futurePriceName = plans.find(plan => plan.price_id === phase.items[0].price)?.price_name;
          }
          changeDate = new Date(phase.start_date * 1000);
          break;
        }
      }
      if (futurePriceId) {
        ret.description = `Your subscription will be upgraded to the ${futurePriceName} plan on ${changeDate!.toLocaleDateString()}.`;
        ret.scheduled = {
          price_id: futurePriceId,
          price_name: futurePriceName!,
          start_date: changeDate!,
          end_date: changeDate! 
        }
      }
    }
    return ret;
  });

}
